import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { useFocusOut } from '../../../hooks';
import { NavigationMenuDesktopProps } from './NavigationMenuDesktop.types';
import {
  StyledSubMenu,
  StyledSubMenuItem,
  StyledMainMenuItem,
  StyledMainMenu,
  StyledNavigationMenuDesktop,
} from './NavigationMenuDesktop.styles';

export const NavigationMenuDesktop: FC<NavigationMenuDesktopProps> = ({
  sectionNavigationItems = [],
  activeMenuItem,
  navigationAriaLabel,
  trackingProperties,
}) => {
  const [selectedMainMenuItemIndex, setSelectedMainMenuItemIndex] = useState<number>(-1);
  const mainMenuRef = useRef<HTMLDivElement | null>(null);

  const toggleBodyScroll = (disable: boolean) => {
    document.body.style.overflow = disable ? 'hidden' : 'inherit';
  };

  const handleFocusOut = (isFocusOut: boolean) => {
    if (isFocusOut) {
      setSelectedMainMenuItemIndex(-1);
      toggleBodyScroll(false);
    }
  };

  useFocusOut({ ref: mainMenuRef.current, onFocusOut: handleFocusOut });

  useEffect(() => {
    return () => {
      toggleBodyScroll(false);
    };
  }, []);

  return (
    <StyledNavigationMenuDesktop ref={mainMenuRef}>
      <nav aria-label={navigationAriaLabel} data-testid="navigation-menu-desktop">
        <div className="container">
          <StyledMainMenu>
            {sectionNavigationItems.map((mainMenuItem, menuItemIndex: number) => (
              <StyledMainMenuItem
                id={`main-menu-item-${menuItemIndex}`}
                key={`main-menu-item-${menuItemIndex}`}
                onClick={() => {
                  setSelectedMainMenuItemIndex(menuItemIndex);
                  toggleBodyScroll(true);
                }}
                className={clsx(
                  selectedMainMenuItemIndex === menuItemIndex && 'selected',
                  mainMenuItem.menuItemLabel === activeMenuItem.menuLabel && 'active'
                )}
                data-testid={`main-menu-item-${menuItemIndex}`}
              >
                <Button
                  isLink={!mainMenuItem.sectionNavigationSubItems.length}
                  href={
                    // eslint-disable-next-line no-script-url
                    mainMenuItem.sectionNavigationSubItems.length ? 'javascript:void(0)' : mainMenuItem.menuItemPath
                  }
                  className="main-menu-item"
                  aria-expanded="false"
                  aria-controls={`main-menu-item-${menuItemIndex}`}
                  trackingProperties={trackingProperties}
                >
                  {mainMenuItem.menuItemLabel}
                </Button>
                {mainMenuItem.sectionNavigationSubItems.length ? (
                  <StyledSubMenu className="sub-menu" aria-label={mainMenuItem.ariaLabel}>
                    {mainMenuItem.sectionNavigationSubItems.map((subMenuItem, subMenuItemIndex) => (
                      <StyledSubMenuItem
                        id={`sub_menu_item_${menuItemIndex}${subMenuItemIndex}`}
                        key={`sub-menu-item-${menuItemIndex}${subMenuItemIndex}`}
                        className={clsx(
                          mainMenuItem.menuItemLabel === activeMenuItem.menuLabel &&
                            subMenuItem.subMenuItemLabel === activeMenuItem.subMenuLabel &&
                            'selected'
                        )}
                      >
                        <Button
                          isLink={true}
                          className="sub-menu-item"
                          href={subMenuItem.subMenuItemPath}
                          aria-expanded="false"
                          aria-controls={`sub_menu_item_${menuItemIndex}${subMenuItemIndex}`}
                          custom_click_track_value={`${mainMenuItem.menuItemLabel} | ${subMenuItem.subMenuItemLabel} |internal`}
                          trackingProperties={trackingProperties}
                        >
                          {subMenuItem.subMenuItemLabel}
                        </Button>
                      </StyledSubMenuItem>
                    ))}
                  </StyledSubMenu>
                ) : null}
              </StyledMainMenuItem>
            ))}
          </StyledMainMenu>
        </div>
      </nav>
    </StyledNavigationMenuDesktop>
  );
};
